import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { getBackendUrl } from '../../services/origin';
import { apiGet, apiPost, apiPut, apiDelete } from '../../services/publicApi';
import {
  Bug, AlertTriangle, AlertCircle, Info, RefreshCw, ChevronDown, ChevronRight,
  Image, Volume2, Globe, User, Clock, CheckCircle, Loader2, Trash2, MessageSquare
} from 'lucide-react';

const API = getBackendUrl();
const getToken = () => localStorage.getItem('admin_token');
// Thin shim that preserves the existing `api(path, opts)` callsites
// throughout this file. Delegates to the unified axios-based wrapper
// (immune to the "body stream already read" race on Chrome) and
// returns parsed JSON to match the previous fetch-based behaviour.
const api = async (path, opts = {}) => {
  const method = (opts.method || 'GET').toUpperCase();
  const headers = { 'Authorization': `Bearer ${getToken()}`, ...(opts.headers || {}) };
  const body = opts.body ? (typeof opts.body === 'string' ? JSON.parse(opts.body) : opts.body) : undefined;
  const url = `/api${path}`;
  let res;
  if (method === 'POST') res = await apiPost(url, body, { headers });
  else if (method === 'PUT') res = await apiPut(url, body, { headers });
  else if (method === 'DELETE') res = await apiDelete(url, { headers });
  else res = await apiGet(url, { headers });
  return res.data;
};

const PRIORITY = {
  critico: { label: 'Critico', icon: AlertTriangle, color: '#EF4444', bg: '#FEE2E2' },
  importante: { label: 'Importante', icon: AlertCircle, color: '#F59E0B', bg: '#FEF3C7' },
  minore: { label: 'Minore', icon: Info, color: '#3B82F6', bg: '#DBEAFE' },
};

const STATUS = {
  new: { label: 'Nuovo', color: '#EF4444' },
  in_progress: { label: 'In lavorazione', color: '#F59E0B' },
  resolved: { label: 'Risolto', color: '#10B981' },
  closed: { label: 'Chiuso', color: '#64748B' },
};

const ROLE_LABELS = { admin: 'Brand Admin', staff: 'Staff', cliente: 'Cliente', superadmin: 'Superadmin' };

export default function SuperadminBugReports() {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('new');
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    const [rRes, sRes] = await Promise.all([api('/superadmin/bug-reports'), api('/superadmin/bug-reports/stats')]);
    setReports(rRes.reports || []);
    setStats(sRes);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await api(`/superadmin/bug-reports/${id}`, { method: 'PUT', body: JSON.stringify({ status }) });
    toast.success(`Stato aggiornato: ${STATUS[status]?.label}`);
    load();
  };

  const saveNotes = async (id, notes) => {
    await api(`/superadmin/bug-reports/${id}`, { method: 'PUT', body: JSON.stringify({ admin_notes: notes }) });
    toast.success('Note salvate');
  };

  const deleteReport = async (id) => {
    if (!window.confirm('Eliminare questa segnalazione?')) return;
    await api(`/superadmin/bug-reports/${id}`, { method: 'DELETE' });
    toast.success('Eliminata');
    load();
  };

  const filtered = reports.filter(r => filter === 'all' ? true : r.status === filter);

  if (loading) return <div className="p-8 flex justify-center"><div className="w-8 h-8 border-3 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8" data-testid="superadmin-bug-reports">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1A1A1A]">Segnalazioni Bug</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {stats.critical_new > 0 && <span className="text-red-500 font-semibold mr-2">{stats.critical_new} critici!</span>}
            {stats.new || 0} nuove · {stats.in_progress || 0} in lavorazione · {stats.resolved || 0} risolte
          </p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><RefreshCw size={18} /></button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { key: 'new', label: `Nuove (${stats.new || 0})` },
          { key: 'in_progress', label: 'In lavorazione' },
          { key: 'resolved', label: 'Risolte' },
          { key: 'closed', label: 'Chiuse' },
          { key: 'all', label: 'Tutte' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              filter === f.key ? 'bg-[#D44E20] text-white' : 'bg-slate-100 text-slate-500'
            }`}>{f.label}</button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
          <Bug size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">Nessuna segnalazione {filter !== 'all' ? STATUS[filter]?.label.toLowerCase() : ''}</p>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map(report => {
          const pr = PRIORITY[report.priority] || PRIORITY.importante;
          const st = STATUS[report.status] || STATUS.new;
          const PrIcon = pr.icon;
          const isOpen = expanded[report.id];
          const time = report.created_at ? new Date(report.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

          return (
            <div key={report.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`report-${report.id}`}>
              <button onClick={() => setExpanded(e => ({ ...e, [report.id]: !isOpen }))}
                className="w-full px-5 py-4 flex items-start justify-between text-left">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: pr.bg }}>
                    <PrIcon size={15} style={{ color: pr.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: pr.bg, color: pr.color }}>{pr.label}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: st.color + '18', color: st.color }}>{st.label}</span>
                      <span className="text-[10px] text-slate-400 flex items-center gap-1"><User size={9} /> {ROLE_LABELS[report.user_role] || report.user_role}</span>
                      {report.user_name && <span className="text-[10px] text-slate-500 font-medium">{report.user_name}</span>}
                      {report.screenshot_url && <Image size={11} className="text-slate-400" />}
                      {report.audio_url && <Volume2 size={11} className="text-slate-400" />}
                    </div>
                    <p className="text-sm text-[#1A1A1A] truncate">{report.description || '(solo audio)'}</p>
                    <span className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5"><Clock size={9} /> {time}</span>
                  </div>
                </div>
                {isOpen ? <ChevronDown size={14} className="text-slate-400 mt-1" /> : <ChevronRight size={14} className="text-slate-400 mt-1" />}
              </button>

              {isOpen && (
                <div className="px-5 pb-5 border-t border-slate-100 space-y-4 pt-4">
                  {report.description && (
                    <div>
                      <label className="text-[10px] text-slate-400 font-semibold block mb-1">Descrizione</label>
                      <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap bg-slate-50 rounded-lg p-3">{report.description}</p>
                    </div>
                  )}

                  {report.page_url && (
                    <div className="flex items-center gap-2 text-xs">
                      <Globe size={12} className="text-slate-400" />
                      <a href={report.page_url} target="_blank" rel="noopener noreferrer" className="text-[#D44E20] hover:underline truncate">{report.page_url}</a>
                    </div>
                  )}

                  <div className="flex gap-3 flex-wrap">
                    {report.screenshot_url && (
                      <div>
                        <label className="text-[10px] text-slate-400 font-semibold block mb-1">Screenshot</label>
                        <a href={report.screenshot_url} target="_blank" rel="noopener noreferrer">
                          <img src={report.screenshot_url} alt="Screenshot" className="w-64 h-36 object-cover rounded-lg border border-slate-200 hover:shadow-md transition-shadow" />
                        </a>
                      </div>
                    )}
                    {report.audio_url && (
                      <div>
                        <label className="text-[10px] text-slate-400 font-semibold block mb-1">Audio</label>
                        <audio controls src={report.audio_url} className="h-10" />
                      </div>
                    )}
                  </div>

                  {/* Admin notes */}
                  <div>
                    <label className="text-[10px] text-slate-400 font-semibold block mb-1">Note admin</label>
                    <textarea defaultValue={report.admin_notes || ''} rows={2}
                      onBlur={e => { if (e.target.value !== (report.admin_notes || '')) saveNotes(report.id, e.target.value); }}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:border-[#D44E20] outline-none"
                      placeholder="Aggiungi note..." />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 flex-wrap">
                    {report.status !== 'in_progress' && report.status !== 'resolved' && (
                      <button onClick={() => updateStatus(report.id, 'in_progress')}
                        className="px-3 py-1.5 bg-amber-50 text-amber-700 text-xs font-semibold rounded-lg hover:bg-amber-100">
                        In lavorazione
                      </button>
                    )}
                    {report.status !== 'resolved' && (
                      <button onClick={() => updateStatus(report.id, 'resolved')}
                        className="px-3 py-1.5 bg-green-50 text-green-700 text-xs font-semibold rounded-lg hover:bg-green-100">
                        Segna risolto
                      </button>
                    )}
                    {report.status !== 'closed' && (
                      <button onClick={() => updateStatus(report.id, 'closed')}
                        className="px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-lg hover:bg-slate-200">
                        Chiudi
                      </button>
                    )}
                    <button onClick={() => deleteReport(report.id)}
                      className="px-3 py-1.5 text-red-500 text-xs font-semibold rounded-lg hover:bg-red-50">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
